<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 class="page-title">Orçamentos</h1>
        <p class="nd-page-kicker">Propostas, valores e condições comerciais.</p>
      </div>
      <v-btn color="primary" elevation="0" to="/orcamentos/novo">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid module="proposals" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Orçamentos</div>
          <div class="nd-table-toolbar__subtitle">Filtre e acompanhe propostas por paciente, status e pagamento.</div>
        </div>
        <v-form class="nd-table-toolbar__actions nd-table-toolbar__actions--wide" @submit.prevent="load()">
          <v-text-field
            dense
            outlined hide-details="auto"
            placeholder="Buscar paciente"
            prepend-inner-icon="mdi-magnify"
            v-model="dataList.filter.patientName"
          ></v-text-field>
          <v-select dense outlined hide-details="auto" placeholder="Status" v-model="dataList.filter.status" :items="statusOptions" item-text="label" item-value="value" clearable></v-select>
          <v-select dense outlined hide-details="auto" placeholder="Forma de pagamento" v-model="dataList.filter.paymentMethod" :items="paymentMethodOptions" item-text="label" item-value="value" clearable></v-select>
          <v-btn type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="dataList.headers"
              :items="dataList.data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="dataList.count"
              :disable-sort="true"
              :loading="dataList.loading"
              no-data-text="Nenhum orçamento encontrado"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="dataList.options"
              :footer-props="dataList.footerOptions"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="statusColor(item.status)" dark>
                  {{ statusLabel(item.status) }}
                </v-chip>
              </template>

              <template v-slot:[`item.paymentMethod`]="{ item }">
                {{ paymentMethodLabel(item.paymentMethod) }}
              </template>

              <template v-slot:[`item.totalValue`]="{ item }">
                {{ item.totalValue != null ? 'R$ ' + Number(item.totalValue).toFixed(2) : '-' }}
              </template>

              <template v-slot:[`item.patient`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ item.patient && item.patient.name ? item.patient.name.charAt(0) : "O" }}</span>
                  <div>
                    <strong>{{ item.patient ? item.patient.name : '-' }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.date`]="{ item }">
                {{ item.date ? new Date(item.date).toLocaleDateString('pt-BR') : '-' }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewItem(item)">
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar orçamento</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deleteProposal(item)">
                        <v-icon size="19">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover orçamento</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "ProposalsList",

  data: () => ({
    statusOptions: [
      { label: "Pendente",   value: 1 },
      { label: "Aprovado",   value: 2 },
      { label: "Rejeitado",  value: 3 },
      { label: "Aguardando", value: 4 },
    ],
    paymentMethodOptions: [
      { label: "Dinheiro",          value: 1 },
      { label: "Cartão de Crédito", value: 2 },
      { label: "Cartão de Débito",  value: 3 },
      { label: "Boleto",            value: 4 },
      { label: "Pix",               value: 5 },
    ],
    dataList: {
      filter: {
        patientName: "",
        status: null,
        paymentMethod: null,
      },
      headers: [
        { text: "Paciente",        value: "patient" },
        { text: "Valor",           value: "totalValue" },
        { text: "Status",          value: "status" },
        { text: "Forma Pagamento", value: "paymentMethod" },
        { text: "Data",            value: "date", sortable: false },
        { text: "",                value: "actions", sortable: false, align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: { page: 1, itemsPerPage: 10 },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
  }),

  created() {
    this.load();
  },

  computed: {
    totalListedValue() {
      const total = this.dataList.data.reduce((sum, item) => sum + Number(item.totalValue || 0), 0);
      return `R$ ${total.toFixed(2)}`;
    },
  },

  methods: {
    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.itemsPerPage = size;
      this.load();
    },

    async load() {
      this.dataList.loading = true;
      try {
        const { page, itemsPerPage } = this.dataList.options;
        const limit = itemsPerPage > 0 ? itemsPerPage : 10;
        const start = (page - 1) * limit;

        const params = { start, limit };
        Object.entries(this.dataList.filter).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") params[key] = value;
        });

        const result = await axios.get("/proposals", { params });
        this.dataList.data  = result.data.data;
        this.dataList.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error(
          "Ocorreu um erro ao carregar os orçamentos.",
          { position: "top-right", timeout: 5000 }
        );
      } finally {
        this.dataList.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/orcamentos/${item.id}`);
    },

    async deleteProposal(item) {
      const patientName = item.patient ? item.patient.name : `#${item.id}`;
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover o orçamento de "${patientName}"?`,
        title: "Confirmar Exclusão",
        actions: { false: "Cancelar", true: { text: "Remover", color: "error" } },
      });

      if (!proceed) return;

      this.dataList.loading = true;
      try {
        await axios.delete(`/proposals/${item.id}`);
        this.$dialog.notify.info("Orçamento removido com sucesso!", { position: "top-right", timeout: 2000 });
        this.load();
      } catch (error) {
        this.$dialog.notify.error("Erro ao remover orçamento.", { position: "top-right", timeout: 5000 });
      } finally {
        this.dataList.loading = false;
      }
    },

    statusLabel(status) {
      const map = { 1: "Pendente", 2: "Aprovado", 3: "Rejeitado", 4: "Aguardando" };
      return map[status] || "Desconhecido";
    },

    statusColor(status) {
      const map = { 1: "orange", 2: "green", 3: "red", 4: "blue" };
      return map[status] || "grey";
    },

    paymentMethodLabel(method) {
      const map = { 1: "Dinheiro", 2: "Cartão de Crédito", 3: "Cartão de Débito", 4: "Boleto", 5: "Pix" };
      return map[method] || "-";
    },
  },
};
</script>
